import { useState, useEffect, useRef, FormEvent } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../stores/authStore';
import { auth } from '../../services/api';
import { showInfo } from '../../utils/alerts';
import { LoginGatewayLoader, LoginGatewayStep } from './LoginGatewayLoader';
import { useLanguage } from '../../i18n/LanguageContext';

export function LoginPage() {
  const { setSession, error: authError, clearError } = useAuth();
  const { t } = useLanguage();
  const params = useParams<{ tenantSlug?: string }>();
  const [searchParams] = useSearchParams();

  const urlSlug = params.tenantSlug || searchParams.get('tenant') || searchParams.get('org') || searchParams.get('slug') || '';
  const isLockedToOrg = Boolean(urlSlug);

  const [email, setEmail] = useState('learner@demo.trainova.local');
  const [password, setPassword] = useState('Trainova@Learner2026!');
  const [tenantSlug, setTenantSlug] = useState(urlSlug || 'demo-trainova');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);

  // Portal Entrance Transition States
  const [isEntering, setIsEntering] = useState(false);
  const [gatewayStep, setGatewayStep] = useState<LoginGatewayStep>('authenticating');
  const [gatewayProgress, setGatewayProgress] = useState(15);
  const [welcomeInfo, setWelcomeInfo] = useState<{
    userName?: string;
    role?: string;
    tenantSlug?: string;
  }>({});

  const timerRefs = useRef<NodeJS.Timeout[]>([]);

  const clearTimers = () => {
    timerRefs.current.forEach(clearTimeout);
    timerRefs.current = [];
  };

  useEffect(() => {
    return () => clearTimers();
  }, []);

  useEffect(() => {
    if (urlSlug) {
      setTenantSlug(urlSlug);
    }
  }, [urlSlug]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearTimers();
    setLocalError(null);
    clearError();

    if (!email.trim() || !password) {
      setLocalError(t('auth.err_required', undefined, 'Please enter your email and password.'));
      return;
    }

    // Determine initial role prediction from email
    let predictedRole = 'LEARNER';
    if (email.includes('admin@') || email.includes('superadmin')) {
      predictedRole = 'PLATFORM_SUPER_ADMIN';
    } else if (email.includes('tenantadmin')) {
      predictedRole = 'TENANT_ADMIN';
    } else if (email.includes('trainer') || email.includes('instructor')) {
      predictedRole = 'TRAINER';
    }

    setWelcomeInfo({
      userName: email.split('@')[0].replace(/[._-]/g, ' '),
      role: predictedRole,
      tenantSlug: urlSlug || tenantSlug || 'Trainova',
    });

    // Launch Entrance Overlay
    setIsEntering(true);
    setGatewayStep('authenticating');
    setGatewayProgress(20);

    // Stagger smooth progress ticks for visual reassurance
    const t1 = setTimeout(() => {
      setGatewayStep('connecting');
      setGatewayProgress(45);
    }, 400);
    timerRefs.current.push(t1);

    const t2 = setTimeout(() => {
      setGatewayStep('preparing');
      setGatewayProgress(70);
    }, 850);
    timerRefs.current.push(t2);

    try {
      const res = await auth.login({
        email: email.trim(),
        password,
        tenantSlug: (urlSlug || tenantSlug).trim() || undefined,
        rememberMe,
      });

      if (res.success && res.data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = res.data as any;
        const token: string = data.token ?? data.accessToken ?? data.access_token ?? '';
        const userObj = data.user || {};
        const tenantObj = data.tenant || {};

        if (!token) {
          throw new Error('Invalid authentication response from server.');
        }

        // Update welcome details from server response
        setWelcomeInfo({
          userName: userObj.name || (userObj.email ? userObj.email.split('@')[0] : email.split('@')[0]),
          role: userObj.role || predictedRole,
          tenantSlug: tenantObj.slug || urlSlug || tenantSlug,
        });

        // Stage 4: Welcome reveal
        setGatewayStep('welcome');
        setGatewayProgress(95);

        // Smooth buffer to appreciate welcome message
        const t3 = setTimeout(() => {
          setGatewayProgress(100);
          setGatewayStep('redirecting');
          const t4 = setTimeout(() => {
            setSession(token, data.refreshToken);
          }, 350);
          timerRefs.current.push(t4);
        }, 750);
        timerRefs.current.push(t3);
      } else {
        clearTimers();
        setIsEntering(false);
        const rawMsg = res.error?.message || '';
        const isAuth401 = res.error?.code === 'UNAUTHORIZED' || res.error?.code === 'INVALID_CREDENTIALS' || res.error?.code === 'HTTP_401' || rawMsg.toLowerCase().includes('invalid');
        setLocalError(
          isAuth401
            ? t('auth.err_invalid_credentials', undefined, 'Invalid email or password. Please check your credentials or click a Quick Demo role above.')
            : rawMsg || t('auth.err_login_failed', undefined, 'Login failed. Please check your credentials.')
        );
      }
    } catch (err: any) {
      clearTimers();
      setIsEntering(false);
      setLocalError(err.message || t('auth.err_unexpected', undefined, 'An unexpected error occurred. Please try again.'));
    }
  };

  const displayError = localError || authError;

  return (
    <div className="login-card">
      <div className="login-header">
        <div className="login-header__badge">
          <span className="login-header__badge-dot" />
          <span>{t('auth.studio_badge', undefined, 'AI Simulation Studio')}</span>
        </div>
        <h1 className="login-header__title">
          {isLockedToOrg
            ? t('auth.sign_in_to_org', { org: urlSlug }, `Sign in to ${urlSlug}`)
            : t('auth.welcome_back', undefined, 'Sign in to Trainova')}
        </h1>
        <p className="login-header__sub">
          {isLockedToOrg
            ? t('auth.org_portal_welcome', { org: urlSlug }, `Welcome to the ${urlSlug} organization portal.`)
            : t('auth.sign_in_subtitle', undefined, 'Sign in to access your AI simulation workspace')}
        </p>
      </div>

      {/* Organization Lock Banner when URL has tenant */}
      {isLockedToOrg ? (
        <div className="login-org-pill">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <span style={{ fontSize: '20px', flexShrink: 0 }}>🏢</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {urlSlug}
              </div>
              <div style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                🔒 {t('auth.single_tenant_workspace', undefined, 'Single-tenant workspace')}
              </div>
            </div>
          </div>
          <Link
            to="/login"
            style={{ fontSize: '12px', color: 'var(--blue, #2563eb)', fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}
            title={t('auth.change_org_title', undefined, 'Switch to default login')}
          >
            {t('auth.change_org', undefined, 'Change')}
          </Link>
        </div>
      ) : (
        <div className="login-demo-roles">
          <div className="login-demo-roles__label">{t('auth.quick_demo_access', undefined, 'Quick Demo Access')}</div>
          <div className="login-demo-roles__tabs" role="tablist" aria-label={t('auth.demo_role_selector_aria', undefined, 'Demo role selector')}>
            <button
              type="button"
              role="tab"
              aria-selected={email.includes('learner')}
              className={`login-demo-roles__tab ${email.includes('learner') ? 'active' : ''}`}
              onClick={() => {
                setEmail('learner@demo.trainova.local');
                setPassword('Trainova@Learner2026!');
                setTenantSlug('demo-trainova');
                clearError();
                setLocalError(null);
              }}
            >
              <span className="login-demo-roles__icon">🎓</span>
              <span className="login-demo-roles__text">{t('auth.role_learner', undefined, 'Learner')}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={email.includes('tenantadmin')}
              className={`login-demo-roles__tab ${email.includes('tenantadmin') ? 'active' : ''}`}
              onClick={() => {
                setEmail('tenantadmin@demo.trainova.local');
                setPassword('Trainova@Tenant2026!');
                setTenantSlug('demo-trainova');
                clearError();
                setLocalError(null);
              }}
            >
              <span className="login-demo-roles__icon">🏢</span>
              <span className="login-demo-roles__text">{t('auth.role_admin', undefined, 'Admin')}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={email === 'admin@trainova.local'}
              className={`login-demo-roles__tab ${email === 'admin@trainova.local' ? 'active' : ''}`}
              onClick={() => {
                setEmail('admin@trainova.local');
                setPassword('Trainova@Admin2026!');
                setTenantSlug('demo-trainova');
                clearError();
                setLocalError(null);
              }}
            >
              <span className="login-demo-roles__icon">👑</span>
              <span className="login-demo-roles__text">{t('auth.role_super_admin', undefined, 'Super Admin')}</span>
            </button>
          </div>
        </div>
      )}

      {displayError && (
        <div className="alert-banner alert-error" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 14px', borderRadius: '10px', background: 'var(--bad-soft, #fef2f2)', color: 'var(--bad, #dc2626)', border: '1px solid #F1C7C7', fontSize: '13px' }} role="alert">
          <span>⚠️</span>
          <span>{displayError}</span>
        </div>
      )}

      {/* Futuristic Fullscreen AI Gateway Entrance Transition Loader */}
      {isEntering && (
        <LoginGatewayLoader
          step={gatewayStep}
          progress={gatewayProgress}
          email={email}
          role={welcomeInfo.role}
          tenantSlug={welcomeInfo.tenantSlug}
          userName={welcomeInfo.userName}
        />
      )}

      <form onSubmit={handleSubmit} noValidate className="login-form">
        <div className="login-field">
          <label htmlFor="email" className="login-label">
            {t('auth.email_label', undefined, 'Email Address / User ID')}
          </label>
          <div className="login-input-wrap">
            <span className="login-input-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </span>
            <input
              id="email"
              type="email"
              className="login-input"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck="false"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.email_placeholder', undefined, 'learner@enterprise.com')}
              disabled={isEntering}
            />
          </div>
        </div>

        <div className="login-field">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label htmlFor="password" className="login-label">
              {t('auth.password_label', undefined, 'Password')}
            </label>
            <a
              href="#forgot"
              className="login-forgot-link"
              onClick={(e) => {
                e.preventDefault();
                showInfo(
                  t('auth.reset_password_title', undefined, 'Reset Password'),
                  t('auth.reset_password_msg', undefined, 'Password reset instructions have been dispatched to your email.')
                );
              }}
            >
              {t('auth.forgot_password', undefined, 'Forgot password?')}
            </a>
          </div>
          <div className="login-input-wrap">
            <span className="login-input-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </span>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className="login-input has-toggle"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.password_placeholder', undefined, '••••••••••••')}
              disabled={isEntering}
            />
            <button
              type="button"
              className="login-reveal-btn"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('auth.hide_password_aria', undefined, 'Hide password') : t('auth.show_password_aria', undefined, 'Show password')}
            >
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Organization Slug Field */}
        <div className="login-field">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label htmlFor="tenantSlug" className="login-label">
              {t('auth.tenant_slug_label', undefined, 'Organization Slug')}
            </label>
            {isLockedToOrg && (
              <span style={{ color: 'var(--ok, #16a34a)', fontSize: '11px', fontWeight: 600 }}>
                🔒 {t('auth.locked_by_url', undefined, 'Locked by URL')}
              </span>
            )}
          </div>
          <div className="login-input-wrap">
            <span className="login-input-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="16" height="20" x="4" y="2" rx="2" />
                <path d="M9 22v-4h6v4" />
                <path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01" />
              </svg>
            </span>
            <input
              id="tenantSlug"
              type="text"
              className="login-input"
              autoComplete="organization"
              value={isLockedToOrg ? urlSlug : tenantSlug}
              onChange={(e) => !isLockedToOrg && setTenantSlug(e.target.value)}
              placeholder={t('auth.tenant_slug_placeholder', undefined, 'e.g. demo-trainova')}
              readOnly={isLockedToOrg}
              disabled={isEntering || isLockedToOrg}
            />
          </div>
        </div>

        <label className="login-remember">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="login-remember__check"
          />
          <span>{t('auth.remember_me', undefined, 'Keep me signed in on this device')}</span>
        </label>

        <button
          type="submit"
          className="login-submit-btn"
          disabled={isEntering || !email || !password}
        >
          <span>
            {isEntering
              ? t('auth.entering_portal', undefined, 'Entering Trainova.ai…')
              : isLockedToOrg
                ? t('auth.submit_sign_in_to_org', { org: urlSlug }, `Sign In to ${urlSlug}`)
                : t('auth.submit_sign_in', undefined, 'Sign In')}
          </span>
          {!isEntering && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          )}
        </button>
      </form>

      <p className="login-bottom-note">
        {t('auth.bottom_note', undefined, 'Enterprise assessment portal · Accounts provisioned by your administrator')}
      </p>
    </div>
  );
}

